import React, { useState, useEffect, useMemo } from 'react'
import { Heading, Box, Stack, Loading, Badge, EmptyBox, SimpleTabs, SimpleStat, SimpleGrid, Text, ImageLogo } from '@/components/lib'
import SimpleLineChart from '@/components/lib/SimpleLineChart'

import api from '../../../api'
import utils from '@/utils/utils'
import DataImage from '../../../images/dataProcessing.png'
import GarminLogo from '../../../images/garmin.png'
import DexcomLogo from '../../../images/dexcom.png'

export default function HardwareResponses ({ trialId, participantId }) {
  // Store data for each dataset separately
  const [datasetData, setDatasetData] = useState({})
  const [loadingDatasets, setLoadingDatasets] = useState({})
  const [loadedDatasets, setLoadedDatasets] = useState(new Set())

  // Map of dataset names to their types
  const datasetMap = {
    Summary: 'daily',
    Sleep: 'sleep',
    Activity: 'activity',
    'Blood Glucose': 'egv',
    Epochs: 'epochs',
    'Pulse Ox': 'pulseox',
    'Heart rate variability': 'hrv',
    Stress: 'stress'
  }

  // Fetch data for a specific dataset
  const fetchDataset = (datasetName) => {
    const datasetType = datasetMap[datasetName]
    if (!datasetType || loadedDatasets.has(datasetName)) {
      return // Already loaded or invalid dataset
    }

    setLoadingDatasets(prev => ({ ...prev, [datasetName]: true }))
    api.trials.getParticipantHardwareResponses(
      trialId,
      participantId,
      d => {
        setLoadingDatasets(prev => ({ ...prev, [datasetName]: false }))
        setDatasetData(prev => ({ ...prev, [datasetName]: d.hardwareResponses || [] }))
        setLoadedDatasets(prev => new Set([...prev, datasetName]))
      },
      err => {
        setLoadingDatasets(prev => ({ ...prev, [datasetName]: false }))
        utils.toast('error', err.message || 'Failed to load data')
      },
      datasetType
    )
  }

  // Handle tab change - fetch data if not already loaded
  const handleTabChange = (tabTitle) => {
    fetchDataset(tabTitle)
  }

  // Fetch the default Summary tab data on initial mount
  useEffect(() => {
    if (trialId && participantId) {
      fetchDataset('Summary')
    }
  }, [trialId, participantId])

  const SLEEP_SCORE_COLOURS = {
    EXCELLENT: 'green',
    GOOD: 'green',
    FAIR: 'yellow',
    POOR: 'red'
  }

  // Helper function to get filtered data for a dataset
  const getDatasetData = (datasetName) => {
    const data = datasetData[datasetName] || []

    if (datasetName === 'Summary') {
      return data.filter(h => h.type === 'daily')
    } else if (datasetName === 'Sleep') {
      return data.filter(h => h.type === 'sleep')
    } else if (datasetName === 'Activity') {
      return data.filter(h => h.type === 'activity')
    } else if (datasetName === 'Blood Glucose') {
      return data.filter(h => h.type === 'egv' && h.source === 'dexcom')
        ?.sort((a, b) => {
          if (!a.recordSystemTime && !b.recordSystemTime) return 0
          if (!a.recordSystemTime) return 1
          if (!b.recordSystemTime) return -1
          return new Date(b.recordSystemTime) - new Date(a.recordSystemTime)
        })
    } else if (datasetName === 'Epochs') {
      return data.filter(h => h.type === 'epochs')
        ?.sort((a, b) => {
          const aTime = a?.entry?.startTimeInSeconds
          const bTime = b?.entry?.startTimeInSeconds
          if (!aTime && !bTime) return 0
          if (!aTime) return 1
          if (!bTime) return -1
          return bTime - aTime
        })
    } else if (datasetName === 'Pulse Ox') {
      return data.filter(h => h.type === 'pulseox')
        ?.sort((a, b) => {
          const aDate = a?.entry?.calendarDate
          const bDate = b?.entry?.calendarDate
          if (!aDate && !bDate) return 0
          if (!aDate) return 1
          if (!bDate) return -1
          return bDate.localeCompare(aDate)
        })
    } else if (datasetName === 'Heart rate variability') {
      return data.filter(h => h.type === 'hrv')
        ?.sort((a, b) => {
          const aDate = a?.entry?.calendarDate
          const bDate = b?.entry?.calendarDate
          if (!aDate && !bDate) return 0
          if (!aDate) return 1
          if (!bDate) return -1
          return bDate.localeCompare(aDate)
        })
    } else if (datasetName === 'Stress') {
      return data.filter(h => h.type === 'stress')
        ?.sort((a, b) => {
          const aDate = a?.entry?.calendarDate
          const bDate = b?.entry?.calendarDate
          if (!aDate && !bDate) return 0
          if (!aDate) return 1
          if (!bDate) return -1
          return bDate.localeCompare(aDate)
        })
    }

    return data
  }

  // Build tabs array - always show all tabs, they'll load lazily
  const tabs = useMemo(() => {
    const availableTabs = []
    const dailiesData = getDatasetData('Summary') || []
    const sleepsData = getDatasetData('Sleep') || []
    const activitiesData = getDatasetData('Activity') || []
    const egvsData = getDatasetData('Blood Glucose') || []
    const epochsData = getDatasetData('Epochs') || []
    const pulseoxData = getDatasetData('Pulse Ox') || []
    const hrvData = getDatasetData('Heart rate variability') || []
    const stressData = getDatasetData('Stress') || []

    // Summary tab
    availableTabs.push({
      title: 'Summary',
      content: loadingDatasets.Summary
        ? (
          <Box textAlign='center' p={10}>
            <Loading />
          </Box>
          )
        : dailiesData.length > 0
          ? (
            <Stack gap={5}>
              {dailiesData.map((response, index) => (
                <Stack gap={4} key={response._id} p={4} borderWidth='1px' borderRadius='md' bg='bg.surface'>
                  <Box display='flex' justifyContent='space-between' alignItems='center'>
                    <Heading size='sm'>{utils.formatDate(response?.entry?.calendarDate)}</Heading>
                    <Box display='flex' alignItems='center' gap={2}>
                      {response?.source === 'garmin' && <ImageLogo as='span' p={1} display='inline-flex' src={GarminLogo} alt='Garmin' w='30px' h='auto' />}
                      {response?.source && <Badge>Source: {response.source}</Badge>}
                    </Box>
                  </Box>

                  <Stack direction='row' justifyContent='center'>
                    {response?.entry?.activeKilocalories &&
                      <SimpleStat
                        name='Active Calories'
                        number={`${response.entry.activeKilocalories} kCal`}
                      />}
                    {response?.entry?.bmrKilocalories &&
                      <SimpleStat
                        name='Total Calories'
                        number={`${response.entry.bmrKilocalories} kCal`}
                      />}
                  </Stack>
                  <Stack direction='row' textAlign='center'>
                    {response?.entry?.steps &&
                      <SimpleStat
                        name='Steps'
                        number={`${response.entry.steps}`}
                      />}
                    {response?.entry?.activeTimeInSeconds &&
                      <SimpleStat
                        name='Active Time'
                        number={`${response.entry.activeTimeInSeconds} secs`}
                      />}
                    {response?.entry?.distanceInMeters &&
                      <SimpleStat
                        name='Distance'
                        number={`${response.entry.distanceInMeters} m`}
                      />}
                  </Stack>
                  {(() => {
                    // Prepare heart rate chart data if available
                    const heartRateSamples = response?.entry?.timeOffsetHeartRateSamples
                    const startTimeInSeconds = response?.entry?.startTimeInSeconds

                    if (heartRateSamples && startTimeInSeconds) {
                      const entries = Object.entries(heartRateSamples)
                        .map(([offset, hr]) => ({
                          offset: parseInt(offset, 10),
                          hr
                        }))
                        .sort((a, b) => a.offset - b.offset)

                      if (entries.length > 0) {
                        const labels = entries.map(entry => {
                          const timeInSeconds = startTimeInSeconds + entry.offset
                          const date = new Date(timeInSeconds * 1000)
                          return utils.formatDateTime(date)
                        })
                        const hrValues = entries.map(entry => entry.hr)

                        const hrChartData = {
                          labels,
                          datasets: [{
                            label: 'Heart Rate (bpm)',
                            data: hrValues
                          }]
                        }

                        return (
                          <Box p={4} borderWidth='1px' borderRadius='md' borderColor='border.default' h='400px'>
                            <Heading size='sm' mb={4}>Heart Rate Over Time</Heading>
                            <SimpleLineChart
                              labels={hrChartData.labels}
                              datasets={hrChartData.datasets}
                              options={{
                                scales: {
                                  x: {
                                    title: {
                                      text: 'Time',
                                      display: true
                                    }
                                  },
                                  y: {
                                    title: {
                                      text: 'Heart Rate (bpm)',
                                      display: true
                                    }
                                  }
                                }
                              }}
                            />
                          </Box>
                        )
                      }
                    }
                    return null
                  })()}
                </Stack>
              ))}
            </Stack>
            )
          : (
            <EmptyBox
              icon='watch'
              title='No summary data available'
              description='Data from wearables and connected devices will automatically show here once received by Trialflare.'
            />
            )
    })

    // Sleep tab
    availableTabs.push({
      title: 'Sleep',
      content: loadingDatasets.Sleep
        ? (
          <Box textAlign='center' p={10}>
            <Loading />
          </Box>
          )
        : sleepsData.length > 0
          ? (
            <Stack gap={5}>
              {sleepsData.map((response, index) => {
                // Prepare respiration chart data
                const respirationData = response?.entry?.timeOffsetSleepRespiration
                const startTimeInSeconds = response?.entry?.startTimeInSeconds
                let respirationChartData = null

                if (respirationData && startTimeInSeconds) {
                  const entries = Object.entries(respirationData)
                    .map(([offset, value]) => ({
                      offset: parseInt(offset, 10),
                      value
                    }))
                    .sort((a, b) => a.offset - b.offset)

                  if (entries.length > 0) {
                    const labels = entries.map(entry => {
                      const timeInSeconds = startTimeInSeconds + entry.offset
                      const date = new Date(timeInSeconds * 1000)
                      return utils.formatDateTime(date)
                    })
                    const respirationValues = entries.map(entry => entry.value)

                    respirationChartData = {
                      labels,
                      datasets: [{
                        label: 'Respiration Rate (breaths/min)',
                        data: respirationValues
                      }]
                    }
                  }
                }

                // Prepare sleep stages visualization data
                const sleepLevelsMap = response?.entry?.sleepLevelsMap
                const sleepStartTime = response?.entry?.startTimeInSeconds
                const sleepDuration = response?.entry?.durationInSeconds
                let sleepStagesData = null

                if (sleepLevelsMap && sleepStartTime && sleepDuration) {
                  // Create an array of all sleep periods with their types
                  const allPeriods = []

                  if (sleepLevelsMap.deep) {
                    sleepLevelsMap.deep.forEach(period => {
                      allPeriods.push({
                        ...period,
                        type: 'deep',
                        color: '#1e40af' // Blue for deep sleep
                      })
                    })
                  }

                  if (sleepLevelsMap.light) {
                    sleepLevelsMap.light.forEach(period => {
                      allPeriods.push({
                        ...period,
                        type: 'light',
                        color: '#3b82f6' // Lighter blue for light sleep
                      })
                    })
                  }

                  if (sleepLevelsMap.rem) {
                    sleepLevelsMap.rem.forEach(period => {
                      allPeriods.push({
                        ...period,
                        type: 'rem',
                        color: '#8b5cf6' // Purple for REM sleep
                      })
                    })
                  }

                  // Sort by start time
                  allPeriods.sort((a, b) => a.startTimeInSeconds - b.startTimeInSeconds)

                  sleepStagesData = {
                    startTime: sleepStartTime,
                    duration: sleepDuration,
                    periods: allPeriods
                  }
                }

                return (
                  <Stack gap={4} key={response._id} p={4} borderWidth='1px' borderRadius='md' bg='bg.surface'>
                    <Box display='flex' justifyContent='space-between' alignItems='center'>
                      <Heading size='sm'>{utils.formatDate(response?.entry?.calendarDate)}</Heading>
                      <Box display='flex' alignItems='center' gap={2}>
                        {response?.entry?.overallSleepScore && <Badge colorPalette={SLEEP_SCORE_COLOURS[response.entry.overallSleepScore.qualifierKey]}>Score: {response.entry.overallSleepScore?.value} ({response.entry.overallSleepScore?.qualifierKey})</Badge>}
                        {response?.source === 'garmin' && <ImageLogo as='span' p={1} display='inline-flex' src={GarminLogo} alt='Garmin' w='30px' h='auto' />}
                        {response?.source && <Badge>Source: {response.source}</Badge>}
                      </Box>
                    </Box>
                    <Stack direction='row' textAlign='center'>
                      {response?.entry?.durationInSeconds &&
                        <SimpleStat
                          name='Asleep Time'
                          number={`${response.entry.durationInSeconds} seconds`}
                        />}
                      {response?.entry?.awakeDurationInSeconds &&
                        <SimpleStat
                          name='Awake Time'
                          number={`${response.entry.awakeDurationInSeconds} seconds`}
                        />}
                    </Stack>
                    <Stack direction='row' textAlign='center'>
                      {response?.entry?.deepSleepDurationInSeconds &&
                        <SimpleStat
                          name='Deep Sleep'
                          number={`${response.entry.deepSleepDurationInSeconds} seconds`}
                        />}
                      {response?.entry?.remSleepInSeconds &&
                        <SimpleStat
                          name='REM Sleep'
                          number={`${response.entry.remSleepInSeconds} seconds`}
                        />}
                      {response?.entry?.lightSleepDurationInSeconds &&
                        <SimpleStat
                          name='Light Sleep'
                          number={`${response.entry.lightSleepDurationInSeconds} seconds`}
                        />}
                    </Stack>

                    {/* Respiration Chart */}
                    {respirationChartData && (
                      <Box p={4} borderWidth='1px' borderRadius='md' borderColor='border.default' h='400px'>
                        <Heading size='sm' mb={4}>Sleep Respiration Rate Over Time</Heading>
                        <SimpleLineChart
                          labels={respirationChartData.labels}
                          datasets={respirationChartData.datasets}
                          options={{
                            scales: {
                              x: {
                                title: {
                                  text: 'Time',
                                  display: true
                                }
                              },
                              y: {
                                title: {
                                  text: 'Respiration Rate (breaths/min)',
                                  display: true
                                }
                              }
                            }
                          }}
                        />
                      </Box>
                    )}

                    {/* Sleep Stages Visualization */}
                    {sleepStagesData && (
                      <Box p={4} borderWidth='1px' borderRadius='md' borderColor='border.default'>
                        <Heading size='sm' mb={4}>Sleep Stages Timeline</Heading>
                        <Box position='relative' h='40px' bg='bg.subtle' borderRadius='md' overflow='hidden'>
                          {sleepStagesData.periods.map((period, idx) => {
                            const startOffset = period.startTimeInSeconds - sleepStagesData.startTime
                            const periodDuration = period.endTimeInSeconds - period.startTimeInSeconds
                            const startPercent = (startOffset / sleepStagesData.duration) * 100
                            const widthPercent = (periodDuration / sleepStagesData.duration) * 100

                            return (
                              <Box
                                key={idx}
                                position='absolute'
                                left={`${startPercent}%`}
                                width={`${widthPercent}%`}
                                height='100%'
                                bg={period.color}
                                title={`${period.type}: ${utils.formatDateTime(new Date(period.startTimeInSeconds * 1000))} - ${utils.formatDateTime(new Date(period.endTimeInSeconds * 1000))}`}
                                style={{ cursor: 'pointer' }}
                              />
                            )
                          })}
                        </Box>
                        <Stack direction='row' gap={4} mt={3} justifyContent='center'>
                          <Box display='flex' alignItems='center' gap={2}>
                            <Box w='20px' h='20px' bg='#1e40af' borderRadius='sm' />
                            <Text fontSize='sm'>Deep Sleep</Text>
                          </Box>
                          <Box display='flex' alignItems='center' gap={2}>
                            <Box w='20px' h='20px' bg='#3b82f6' borderRadius='sm' />
                            <Text fontSize='sm'>Light Sleep</Text>
                          </Box>
                          <Box display='flex' alignItems='center' gap={2}>
                            <Box w='20px' h='20px' bg='#8b5cf6' borderRadius='sm' />
                            <Text fontSize='sm'>REM Sleep</Text>
                          </Box>
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                )
              })}
            </Stack>
            )
          : (
            <EmptyBox
              icon='watch'
              title='No sleep data available'
              description='Data from wearables and connected devices will automatically show here once received by Trialflare.'
            />
            )
    })

    // Activity tab
    availableTabs.push({
      title: 'Activity',
      content: loadingDatasets.Activity
        ? (
          <Box textAlign='center' p={10}>
            <Loading />
          </Box>
          )
        : activitiesData.length > 0
          ? (
            <Stack gap={5}>
              {activitiesData.map((activity, index) => (
                <Stack gap={4} key={activity._id} p={4} borderWidth='1px' borderRadius='md' bg='bg.surface'>
                  <Box display='flex' justifyContent='space-between' alignItems='center'>
                    <Heading size='sm'>{activity.entry?.activityName} ({utils.formatDate(activity?.entry?.createdAt)})</Heading>
                    <Box display='flex' alignItems='center' gap={2}>
                      {activity?.source === 'garmin' && <ImageLogo as='span' p={1} display='inline-flex' src={GarminLogo} alt='Garmin' w='30px' h='auto' />}
                      {activity?.source && <Badge>Source: {activity.source}</Badge>}
                    </Box>
                  </Box>
                  <Stack direction='row' textAlign='center'>
                    {activity?.entry?.durationInSeconds &&
                      <SimpleStat
                        name='Duration'
                        number={`${activity.entry.durationInSeconds} seconds`}
                      />}
                    {activity?.entry?.averageHeartRateInBeatsPerMinute &&
                      <SimpleStat
                        name='Average Heart Rate'
                        number={`${activity.entry?.averageHeartRateInBeatsPerMinute} bpm`}
                      />}
                    {activity?.entry?.activeKilocalories &&
                      <SimpleStat
                        name='Calories'
                        number={`${activity.entry?.activeKilocalories} kCal`}
                      />}
                  </Stack>
                  <Stack direction='row' textAlign='center'>
                    {activity?.entry?.averageSpeedInMetersPerSecond &&
                      <SimpleStat
                        name='Average speed'
                        number={`${activity.entry?.averageSpeedInMetersPerSecond} m/s`}
                      />}
                    {activity?.entry?.averagePaceInMinutesPerKilometer &&
                      <SimpleStat
                        name='Average pace'
                        number={`${activity.entry?.averagePaceInMinutesPerKilometer} min/km`}
                      />}
                    {activity?.entry?.distanceInMeters &&
                      <SimpleStat
                        name='Distance'
                        number={`${activity.entry?.distanceInMeters} m`}
                      />}
                  </Stack>
                </Stack>
              ))}
            </Stack>
            )
          : (
            <EmptyBox
              icon='watch'
              title='No activity data available'
              description='Data from wearables and connected devices will automatically show here once received by Trialflare.'
            />
            )
    })

    // Blood Glucose tab
    availableTabs.push({
      title: 'Blood Glucose',
      content: loadingDatasets['Blood Glucose']
        ? (
          <Box textAlign='center' p={10}>
            <Loading />
          </Box>
          )
        : egvsData.length > 0
          ? (
            <Stack gap={5}>
              {(() => {
                // Prepare chart data for glucose readings
                const sortedEgvs = [...egvsData].sort((a, b) => {
                  if (!a.recordSystemTime && !b.recordSystemTime) return 0
                  if (!a.recordSystemTime) return 1
                  if (!b.recordSystemTime) return -1
                  return new Date(a.recordSystemTime) - new Date(b.recordSystemTime)
                })
                const validEgvs = sortedEgvs.filter(egv =>
                  egv.recordSystemTime && egv.entry?.value !== undefined
                )
                const chartData = validEgvs.length > 0
                  ? {
                      labels: validEgvs.map(egv => utils.formatDateTime(egv.recordSystemTime)),
                      datasets: [{
                        label: 'Glucose (mg/dL)',
                        data: validEgvs.map(egv => egv.entry.value)
                      }]
                    }
                  : null

                return (
                  <>
                    {chartData && (
                      <Box p={4} borderWidth='1px' borderRadius='md' borderColor='border.default' h='400px'>
                        <Heading size='sm' mb={4}>Glucose Over Time</Heading>
                        <SimpleLineChart
                          labels={chartData.labels}
                          datasets={chartData.datasets}
                          options={{
                            scales: {
                              x: {
                                title: {
                                  text: 'Time',
                                  display: true
                                }
                              },
                              y: {
                                title: {
                                  text: 'Glucose (mg/dL)',
                                  display: true
                                }
                              }
                            }
                          }}
                        />
                      </Box>
                    )}
                    {egvsData.map((egv, index) => (
                      <Stack gap={4} key={egv._id} p={4} borderWidth='1px' borderRadius='md' bg='bg.surface'>
                        <Box display='flex' justifyContent='space-between' alignItems='center'>
                          <Heading size='sm'>
                            {egv.recordSystemTime ? utils.formatDateTime(egv.recordSystemTime) : 'Unknown time'}
                          </Heading>
                          <Box display='flex' alignItems='center' gap={2}>
                            <ImageLogo as='span' p={1} display='inline-flex' src={DexcomLogo} alt='Dexcom' w='30px' h='auto' />
                            <Badge>Source: {egv.source}</Badge>
                          </Box>
                        </Box>
                        <Stack direction='row' textAlign='center' justifyContent='center'>
                          {egv.entry?.value !== undefined && (
                            <SimpleStat
                              name='Glucose Value'
                              number={`${egv.entry.value} mg/dL`}
                            />
                          )}
                          {egv.entry?.trend && (
                            <SimpleStat
                              name='Trend'
                              number={egv.entry.trend}
                            />
                          )}
                        </Stack>
                      </Stack>
                    ))}
                  </>
                )
              })()}
            </Stack>
            )
          : (
            <EmptyBox
              icon='watch'
              title='No blood glucose data available'
              description='Data from wearables and connected devices will automatically show here once received by Trialflare.'
            />
            )
    })

    // Epochs tab
    availableTabs.push({
      title: 'Epochs',
      content: loadingDatasets.Epochs
        ? (
          <Box textAlign='center' p={10}>
            <Loading />
          </Box>
          )
        : epochsData.length > 0
          ? (
            <Stack gap={5}>
              {epochsData.map((epoch, index) => {
                // Convert Unix timestamp (seconds) to Date object (milliseconds)
                const startTime = epoch?.entry?.startTimeInSeconds
                  ? new Date(epoch.entry.startTimeInSeconds * 1000)
                  : null

                return (
                  <Stack gap={4} key={epoch._id} p={4} borderWidth='1px' borderRadius='md' bg='bg.surface'>
                    <Box display='flex' justifyContent='space-between' alignItems='center'>
                      <Heading size='sm'>
                        {startTime ? utils.formatDateTime(startTime) : 'Unknown time'}
                      </Heading>
                      <Box display='flex' alignItems='center' gap={2}>
                        {epoch?.source === 'garmin' && <ImageLogo as='span' p={1} display='inline-flex' src={GarminLogo} alt='Garmin' w='30px' h='auto' />}
                        {epoch?.source && <Badge>Source: {epoch.source}</Badge>}
                      </Box>
                    </Box>
                    <Stack direction='row' textAlign='center' justifyContent='center'>
                      {epoch?.entry?.activityType && (
                        <SimpleStat
                          name='Activity Type'
                          number={epoch.entry.activityType}
                        />
                      )}
                      {epoch?.entry?.intensity && (
                        <SimpleStat
                          name='Intensity'
                          number={epoch.entry.intensity}
                        />
                      )}
                      {epoch?.entry?.activeTimeInSeconds !== undefined && (
                        <SimpleStat
                          name='Active Seconds'
                          number={`${epoch.entry.activeTimeInSeconds} secs`}
                        />
                      )}
                    </Stack>
                    <Stack direction='row' textAlign='center' justifyContent='center'>
                      {epoch?.entry?.meanMotionIntensity !== undefined && (
                        <SimpleStat
                          name='Mean Motion Intensity'
                          number={epoch.entry.meanMotionIntensity}
                        />
                      )}
                      {epoch?.entry?.maxMotionIntensity !== undefined && (
                        <SimpleStat
                          name='Max Motion Intensity'
                          number={epoch.entry.maxMotionIntensity}
                        />
                      )}
                    </Stack>
                  </Stack>
                )
              })}
            </Stack>
            )
          : (
            <EmptyBox
              icon='watch'
              title='No epochs data available'
              description='Data from wearables and connected devices will automatically show here once received by Trialflare.'
            />
            )
    })

    // Pulse Ox tab
    availableTabs.push({
      title: 'Pulse Ox',
      content: loadingDatasets['Pulse Ox']
        ? (
          <Box textAlign='center' p={10}>
            <Loading />
          </Box>
          )
        : pulseoxData.length > 0
          ? (
            <Stack gap={5}>
              {pulseoxData.map((pulseoxEntry, index) => {
                // Prepare chart data for this pulseox entry
                const timeOffsetSpo2Values = pulseoxEntry?.entry?.timeOffsetSpo2Values
                const startTimeInSeconds = pulseoxEntry?.entry?.startTimeInSeconds

                let chartData = null
                if (timeOffsetSpo2Values && startTimeInSeconds) {
                  // Convert timeOffsetSpo2Values object to sorted arrays
                  const entries = Object.entries(timeOffsetSpo2Values)
                    .map(([offset, spo2]) => ({
                      offset: parseInt(offset, 10),
                      spo2
                    }))
                    .sort((a, b) => a.offset - b.offset)

                  if (entries.length > 0) {
                    // Calculate actual times: startTimeInSeconds + offset
                    const labels = entries.map(entry => {
                      const timeInSeconds = startTimeInSeconds + entry.offset
                      const date = new Date(timeInSeconds * 1000)
                      return utils.formatDateTime(date)
                    })
                    const spo2Values = entries.map(entry => entry.spo2)

                    chartData = {
                      labels,
                      datasets: [{
                        label: 'SpO2 (%)',
                        data: spo2Values
                      }]
                    }
                  }
                }

                return (
                  <Stack gap={4} key={pulseoxEntry._id} p={4} borderWidth='1px' borderRadius='md' bg='bg.surface'>
                    <Box display='flex' justifyContent='space-between' alignItems='center'>
                      <Heading size='sm'>
                        {pulseoxEntry?.entry?.calendarDate ? utils.formatDate(pulseoxEntry.entry.calendarDate) : 'Unknown date'}
                      </Heading>
                      <Box display='flex' alignItems='center' gap={2}>
                        {pulseoxEntry?.source === 'garmin' && <ImageLogo as='span' p={1} display='inline-flex' src={GarminLogo} alt='Garmin' w='30px' h='auto' />}
                        {pulseoxEntry?.source && <Badge>Source: {pulseoxEntry.source}</Badge>}
                      </Box>
                    </Box>
                    {chartData && (
                      <Box p={4} borderWidth='1px' borderRadius='md' borderColor='border.default' h='400px'>
                        <Heading size='sm' mb={4}>SpO2 Over Time</Heading>
                        <SimpleLineChart
                          labels={chartData.labels}
                          datasets={chartData.datasets}
                          options={{
                            scales: {
                              x: {
                                title: {
                                  text: 'Time',
                                  display: true
                                }
                              },
                              y: {
                                title: {
                                  text: 'SpO2 (%)',
                                  display: true
                                },
                                min: 0,
                                max: 100
                              }
                            }
                          }}
                        />
                      </Box>
                    )}
                  </Stack>
                )
              })}
            </Stack>
            )
          : (
            <EmptyBox
              icon='watch'
              title='No pulse ox data available'
              description='Data from wearables and connected devices will automatically show here once received by Trialflare.'
            />
            )
    })

    // Heart rate variability tab
    availableTabs.push({
      title: 'Heart rate variability',
      content: loadingDatasets['Heart rate variability']
        ? (
          <Box textAlign='center' p={10}>
            <Loading />
          </Box>
          )
        : hrvData.length > 0
          ? (
            <Stack gap={5}>
              {hrvData.map((hrvEntry, index) => {
                // Prepare chart data for this hrv entry
                const hrvValues = hrvEntry?.entry?.hrvValues
                const startTimeInSeconds = hrvEntry?.entry?.startTimeInSeconds

                let chartData = null
                if (hrvValues && startTimeInSeconds) {
                  // Convert hrvValues object to sorted arrays
                  const entries = Object.entries(hrvValues)
                    .map(([offset, hrvValue]) => ({
                      offset: parseInt(offset, 10),
                      hrvValue
                    }))
                    .sort((a, b) => a.offset - b.offset)

                  if (entries.length > 0) {
                    // Calculate actual times: startTimeInSeconds + offset
                    const labels = entries.map(entry => {
                      const timeInSeconds = startTimeInSeconds + entry.offset
                      const date = new Date(timeInSeconds * 1000)
                      return utils.formatDateTime(date)
                    })
                    const hrvDataValues = entries.map(entry => entry.hrvValue)

                    chartData = {
                      labels,
                      datasets: [{
                        label: 'HRV',
                        data: hrvDataValues
                      }]
                    }
                  }
                }

                return (
                  <Stack gap={4} key={hrvEntry._id} p={4} borderWidth='1px' borderRadius='md' bg='bg.surface'>
                    <Box display='flex' justifyContent='space-between' alignItems='center'>
                      <Heading size='sm'>
                        {hrvEntry?.entry?.calendarDate ? utils.formatDate(hrvEntry.entry.calendarDate) : 'Unknown date'}
                      </Heading>
                      <Box display='flex' alignItems='center' gap={2}>
                        {hrvEntry?.source === 'garmin' && <ImageLogo as='span' p={1} display='inline-flex' src={GarminLogo} alt='Garmin' w='30px' h='auto' />}
                        {hrvEntry?.source && <Badge>Source: {hrvEntry.source}</Badge>}
                      </Box>
                    </Box>
                    <Stack direction='row' textAlign='center' justifyContent='center'>
                      {hrvEntry?.entry?.lastNightAvg !== undefined && (
                        <SimpleStat
                          name='Last Night Avg'
                          number={hrvEntry.entry.lastNightAvg}
                        />
                      )}
                      {hrvEntry?.entry?.lastNight5MinHigh !== undefined && (
                        <SimpleStat
                          name='Last Night 5 Min High'
                          number={hrvEntry.entry.lastNight5MinHigh}
                        />
                      )}
                    </Stack>
                    {chartData && (
                      <Box p={4} borderWidth='1px' borderRadius='md' borderColor='border.default' h='400px'>
                        <Heading size='sm' mb={4}>HRV Over Time</Heading>
                        <SimpleLineChart
                          labels={chartData.labels}
                          datasets={chartData.datasets}
                          options={{
                            scales: {
                              x: {
                                title: {
                                  text: 'Time',
                                  display: true
                                }
                              },
                              y: {
                                title: {
                                  text: 'HRV',
                                  display: true
                                }
                              }
                            }
                          }}
                        />
                      </Box>
                    )}
                  </Stack>
                )
              })}
            </Stack>
            )
          : (
            <EmptyBox
              image={DataImage}
              title='No HRV data available'
              description='Data from wearables and connected devices will automatically show here once received by Trialflare.'
            />
            )
    })

    // Stress tab
    availableTabs.push({
      title: 'Stress',
      content: loadingDatasets.Stress
        ? (
          <Box textAlign='center' p={10}>
            <Loading />
          </Box>
          )
        : stressData.length > 0
          ? (
            <Stack gap={5}>
              {stressData.map((stressEntry, index) => {
                // Prepare chart data for this stress entry
                const stressLevelValues = stressEntry?.entry?.timeOffsetStressLevelValues
                const bodyBatteryValues = stressEntry?.entry?.timeOffsetBodyBatteryValues
                const startTimeInSeconds = stressEntry?.entry?.startTimeInSeconds

                let chartData = null
                if ((stressLevelValues || bodyBatteryValues) && startTimeInSeconds) {
                  // Get all unique offsets from both datasets
                  const allOffsets = new Set()
                  if (stressLevelValues) {
                    Object.keys(stressLevelValues).forEach(offset => allOffsets.add(parseInt(offset, 10)))
                  }
                  if (bodyBatteryValues) {
                    Object.keys(bodyBatteryValues).forEach(offset => allOffsets.add(parseInt(offset, 10)))
                  }

                  const sortedOffsets = Array.from(allOffsets).sort((a, b) => a - b)

                  if (sortedOffsets.length > 0) {
                    // Calculate actual times: startTimeInSeconds + offset
                    const labels = sortedOffsets.map(offset => {
                      const timeInSeconds = startTimeInSeconds + offset
                      const date = new Date(timeInSeconds * 1000)
                      return utils.formatDateTime(date)
                    })

                    const stressData = sortedOffsets.map(offset => {
                      const value = stressLevelValues?.[offset.toString()]
                      // Filter out invalid values (-1, -2)
                      return (value !== undefined && value >= 0) ? value : null
                    })

                    const bodyBatteryData = sortedOffsets.map(offset => {
                      const value = bodyBatteryValues?.[offset.toString()]
                      return (value !== undefined && value >= 0) ? value : null
                    })

                    chartData = {
                      labels,
                      datasets: [
                        {
                          label: 'Stress Level',
                          data: stressData,
                          yAxisID: 'y'
                        },
                        {
                          label: 'Body Battery',
                          data: bodyBatteryData,
                          yAxisID: 'y1'
                        }
                      ]
                    }
                  }
                }

                // Collect body battery events
                const bodyBatteryEvents = []
                if (stressEntry?.entry?.bodyBatteryDynamicFeedbackEvent) {
                  bodyBatteryEvents.push({
                    type: 'Dynamic Feedback',
                    ...stressEntry.entry.bodyBatteryDynamicFeedbackEvent
                  })
                }
                if (stressEntry?.entry?.bodyBatteryActivityEvents) {
                  stressEntry.entry.bodyBatteryActivityEvents.forEach(event => {
                    bodyBatteryEvents.push({
                      type: event.eventType || 'Activity',
                      ...event
                    })
                  })
                }

                return (
                  <Stack gap={4} key={stressEntry._id} p={4} borderWidth='1px' borderRadius='md' bg='bg.surface'>
                    <Box display='flex' justifyContent='space-between' alignItems='center'>
                      <Heading size='sm'>
                        {stressEntry?.entry?.calendarDate ? utils.formatDate(stressEntry.entry.calendarDate) : 'Unknown date'}
                      </Heading>
                      <Box display='flex' alignItems='center' gap={2}>
                        {stressEntry?.source === 'garmin' && <ImageLogo as='span' p={1} display='inline-flex' src={GarminLogo} alt='Garmin' w='30px' h='auto' />}
                        {stressEntry?.source && <Badge>Source: {stressEntry.source}</Badge>}
                      </Box>
                    </Box>
                    {chartData && (
                      <Box p={4} borderWidth='1px' borderRadius='md' borderColor='border.default' h='400px'>
                        <Heading size='sm' mb={4}>Stress Level & Body Battery Over Time</Heading>
                        <SimpleLineChart
                          labels={chartData.labels}
                          datasets={chartData.datasets}
                          options={{
                            scales: {
                              x: {
                                title: {
                                  text: 'Time',
                                  display: true
                                }
                              },
                              y: {
                                type: 'linear',
                                position: 'left',
                                title: {
                                  text: 'Stress Level',
                                  display: true
                                },
                                min: 0,
                                max: 100
                              },
                              y1: {
                                type: 'linear',
                                position: 'right',
                                title: {
                                  text: 'Body Battery',
                                  display: true
                                },
                                min: 0,
                                max: 100,
                                grid: {
                                  drawOnChartArea: false
                                }
                              }
                            }
                          }}
                        />
                      </Box>
                    )}
                    {bodyBatteryEvents.length > 0 && (
                      <Box>
                        <Heading size='sm' mb={3}>Body Battery Events</Heading>
                        <SimpleGrid columns={{ base: 1, md: 3 }} gap={3}>
                          {bodyBatteryEvents.map((event, eventIndex) => {
                            const eventTime = event.eventStartTimeInSeconds
                              ? new Date(event.eventStartTimeInSeconds * 1000)
                              : null

                            return (
                              <Box
                                key={eventIndex}
                                p={3}
                                borderWidth='1px'
                                borderRadius='md'
                                bg='bg.surface'
                                borderColor='border.default'
                              >
                                <Stack gap={2}>
                                  <Badge>{event.type}</Badge>
                                  {eventTime && (
                                    <Text fontSize='sm' color='fg.muted'>
                                      {utils.formatDateTime(eventTime)}
                                    </Text>
                                  )}
                                  {event.bodyBatteryLevel && (
                                    <Text fontSize='sm' fontWeight='medium'>
                                      Level: {event.bodyBatteryLevel}
                                    </Text>
                                  )}
                                  {event.bodyBatteryImpact !== undefined && (
                                    <Text fontSize='sm' fontWeight='medium'>
                                      Impact: {event.bodyBatteryImpact}
                                    </Text>
                                  )}
                                  {event.durationInSeconds !== undefined && (
                                    <Text fontSize='sm' color='fg.muted'>
                                      Duration: {Math.round(event.durationInSeconds / 60)} min
                                    </Text>
                                  )}
                                </Stack>
                              </Box>
                            )
                          })}
                        </SimpleGrid>
                      </Box>
                    )}
                  </Stack>
                )
              })}
            </Stack>
            )
          : (
            <EmptyBox
              image={DataImage}
              title='No stress data available'
              description='Data from wearables and connected devices will automatically show here once received by Trialflare.'
            />
            )
    })

    return availableTabs
  }, [datasetData, loadingDatasets, loadedDatasets, getDatasetData])

  return (
    <Stack gap={5}>
      {tabs.length > 0 && (
        <SimpleTabs
          wrapped
          lazyMount
          tabs={tabs.map(tab => ({
            ...tab,
            content: loadingDatasets[tab.title]
              ? (
                <Box textAlign='center' p={10}>
                  <Loading />
                </Box>
                )
              : (
                  getDatasetData(tab.title)?.length > 0
                    ? (
                        tab.content
                      )
                    : (
                      <EmptyBox
                        icon='watch'
                        title={`No ${tab.title.toLowerCase()} data available`}
                        description='Data from wearables and connected devices will automatically show here once received by Trialflare.'
                      />
                      )
                )
          }))}
          onValueChange={handleTabChange}
        />
      )}

      {tabs.length === 0 && (
        <EmptyBox
          icon='watch'
          title='No hardware data is available yet for this participant'
          description='Data from wearables and connected devices will automatically show here once received by Trialflare.'
        />
      )}
    </Stack>
  )
}
